import { useEffect, useState } from 'react'
import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import type { Lang } from '@/types/lang'
import { translationPublishKey } from '@/features/editor/lib/courseTranslation'
import { useSetTranslationPublishedMutation } from '@/features/editor/coursesApi'
import { useCourseRereadQueued } from './useCourseRereadQueued'

/**
 * Тумблер видимости языка: положение меняется сразу и держится, пока перечитанный курс не скажет
 * то же самое. Пока сервер не ответил, новые клики не принимаются: действие поверх неподтверждённого
 * давало отказы и расхождение экрана с сервером.
 */
export const useTranslationPublishToggle = (courseId: number, lang: Lang, isPublished: boolean) => {
  const [setPublished, { fulfilledTimeStamp }] = useSetTranslationPublishedMutation({
    fixedCacheKey: translationPublishKey(courseId),
  })
  // Ключ общий, а отказ свой: чужой или старый отказ не должен всплывать у этого тумблера.
  const [error, setError] = useState<FetchBaseQueryError | SerializedError | undefined>()
  const rereadQueued = useCourseRereadQueued(courseId, fulfilledTimeStamp ?? 0)
  const [wanted, setWanted] = useState<boolean | null>(null)
  const [sending, setSending] = useState(false)
  const inFlight = sending || rereadQueued

  const send = async (value: boolean): Promise<void> => {
    setSending(true)
    setError(undefined)
    try {
      await setPublished({ courseId, lang, isPublished: value }).unwrap()
    } catch (failure) {
      setError(failure as FetchBaseQueryError | SerializedError)
      setWanted(null)
    }
    setSending(false)
  }

  useEffect(() => {
    if (!inFlight && wanted !== null && wanted === isPublished) setWanted(null)
  }, [inFlight, wanted, isPublished])

  const toggle = (value: boolean) => {
    if (inFlight) return
    setWanted(value)
    void send(value)
  }

  return { checked: wanted ?? isPublished, toggle, error, isSaving: inFlight }
}
